import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  getReport, getTemplateReport, updateReportResponses,
} from '../../../bridge';
import { getCopyrightMessage } from '../../../helpers/itpmetricsUtils';
import { setLanguage } from '../../../helpers/utils';
import { useAuthContext } from '../../../hooks/useAuthContext';
import ContentSection from '../common/ContentSection';
import Loading from '../common/Loading';
import Page from '../common/Page';
import SignedOut from '../common/SignedOutDialog';

import ReportBody from './ReportBody';
import ReportError from './ReportError';
import ReportNotReady from './ReportNotReady';

function Report() {
  const { user } = useAuthContext();
  const [result, setResult] = useState(null);
  const { templateId, respondentId } = useParams();
  const templateMode = !!templateId;
  const [loading, setLoading] = useState(true);
  const [reportError, setReportError] = useState(null);
  const [reportNotReady, setReportNotReady] = useState(null);

  useEffect(() => {
    if (!respondentId && !templateId) {
      return;
    }
    const fetchReport = async () => {
      const params = new URLSearchParams(document.location.search);
      const language = params.get('language');
      if (language) {
        setLanguage(language);
      }
      let r = null;
      if (respondentId) {
        // Cache-aware: do NOT force reload=true on every mount. A forced
        // reload re-runs generateReport, which overwrote respondent.report and
        // wiped the cached individualSynthesis -- so the report (and its PDF
        // download via screenshot) regenerated on every view, capturing the
        // mid-generation state. Regeneration is now an explicit action only.
        r = await getReport(respondentId);
      } else if (templateId) {
        r = await getTemplateReport(templateId);
      }
      await setLoading(false);
      if (r.error) {
        await setReportError(r.error);
        return;
      }
      if (r.response.reportNotReady) {
        setReportNotReady(r.response);
        return;
      }
      await setResult(r.response);
    };
    fetchReport();
  }, [respondentId, templateId]);

  const updateResponse = async (section, subSection, value) => {
    if (templateMode) {
      return;
    }
    const response = await updateReportResponses(respondentId, section, subSection, value);
    if (response.error) {
      await setReportError(response.error);
    }
  };
  if (!user && !templateMode && !(new URLSearchParams(document.location.search).get('token'))) {
    return <SignedOut />;
  }

  if (loading) {
    return <Loading />;
  }

  if (reportError) {
    return <ReportError error={reportError} />;
  }

  if (reportNotReady) {
    return <ReportNotReady report={reportNotReady} />;
  }

  if (!result) {
    return null;
  }

  return (
    <Page>
      <ContentSection>
        <ReportBody
          reportSections={result.reportSections}
          respondent={result.respondent}
          survey={result.survey}
          updateResponse={updateResponse}
          templateMode={templateMode}
        />
        <footer className="justify-content-center">
          {getCopyrightMessage()}
        </footer>
      </ContentSection>
    </Page>
  );
}

export default Report;
